import { useState, useEffect, lazy, Suspense } from 'react';
import { supabase } from '../../utils/supabaseClient';
import config from '../../config';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
	src: string;
	alt: string;
	className?: string;
	objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

const bucketName = config.REACT_APP_SUPABASE_BUCKET_NAME;

const checkFileExists = async (url: string): Promise<boolean> => {
	try {
		const response = await fetch(url, { method: 'HEAD' });
		return response.ok;
	} catch (error) {
		console.error(`Error checking file existence for URL ${url}: ${error}`);
		return false;
	}
};

const fetchImage = async (src: string): Promise<string> => {
	// If the src is an absolute URL, return it as-is
	if (src.startsWith('http://') || src.startsWith('https://')) {
		return src;
	}

	// Remove leading slash if relative path
	let path = src.startsWith('/') ? src.slice(1) : src;

	try {
		const { data } = await supabase.storage.from(bucketName).getPublicUrl(path);

		if (data?.publicUrl) {
			const publicUrlExists = await checkFileExists(data.publicUrl);
			if (publicUrlExists) {
				return data.publicUrl;
			}
			// else {
			// 	console.log(src);
			// }
		}

		// Use local path if Supabase image not found
		const localImageUrl = '/' + path;
		const localFileExists = await checkFileExists(localImageUrl);
		if (localFileExists) {
			return localImageUrl;
		}
	} catch (error: unknown) {
		if (error instanceof Error) {
			console.error(`Error fetching image: ${error.message}`);
		} else {
			console.error('Error fetching image');
		}
	}

	// If no Supabase or local image exists, return placeholder
	return '/img/placeholder.jpg';
};

const Image: React.FC<ImageProps> = ({ src, alt, className, objectFit = 'cover', ...props }) => {
	const [imageUrl, setImageUrl] = useState<string | null>(null);

	useEffect(() => {
		const loadImage = async () => {
			if (!src) {
				setImageUrl('/img/placeholder.jpg');
				return;
			}
			const resolvedUrl = await fetchImage(src);
			setImageUrl(resolvedUrl);
		};

		setImageUrl(null);
		loadImage();
	}, [src]);

	// Render nothing while loading
	if (!imageUrl) {
		return <div className={className} style={props.style} />;
	}

	// Fallback for broken images
	const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		const img = e.target as HTMLImageElement;
		img.src = '/img/placeholder.jpg'; // Use placeholder if the image fails to load
		img.onerror = null; // Prevent infinite loop if placeholder fails as well
	};

	return (
		<img
			src={imageUrl}
			alt={alt}
			className={className}
			style={{ objectFit, ...props.style }}
			onError={handleError}
			loading="lazy"
			{...props}
		/>
	);
};

const LazyImage: React.FC<React.ComponentProps<typeof Image>> = (props) => {
	return (
		<Suspense fallback={<div className={props.className} style={props.style} />}>
			<Image {...props} />
		</Suspense>
	);
};

export default LazyImage;
